/**
 * @fileoverview Create Event Form component.
 * @summary Form for creating new karaoke events.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/CreateEventForm
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'
import Spinner from '../../../components/Spinner'
import type { Venue } from '../../../store/slices/venuesSlice'
import { EventFormFields } from './EventFormFields'

interface CreateEventFormProps {
  formData: {
    name: string
    description: string
    venueId: string
    date: string
    time: string
  }
  isCreating: boolean
  venues: Venue[]
  onFormDataChange: (data: {
    name?: string
    description?: string
    venueId?: string
    date?: string
    time?: string
  }) => void
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
  onDateQuotaError?: (error: { message: string; currentCount?: number; limit?: number }) => void // Callback when quota is exceeded
}

/**
 * @summary Create event form component.
 * @param {CreateEventFormProps} props - Component props.
 * @returns {React.ReactElement} Create event form.
 */
export function CreateEventForm({
  formData,
  isCreating,
  venues,
  onFormDataChange,
  onSubmit,
  onCancel,
  onDateQuotaError,
}: CreateEventFormProps) {
  const { t } = useTranslation()

  return (
    <div className="card mb-2">
      <form onSubmit={onSubmit} className="space-y-6">
        <EventFormFields
          formData={formData}
          venues={venues}
          onFormDataChange={onFormDataChange}
          disabled={isCreating}
          onDateQuotaError={onDateQuotaError}
        />

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-(--color-border)">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isCreating}
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button type="submit" className="btn btn-primary" disabled={isCreating || venues.length === 0}>
            {isCreating ? (
              <>
                <Spinner size="sm" variant="white" />
              </>
            ) : (
              t('events.createButton') || 'Create Event'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

